import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import useClickOutside from "../../helpers/clickOutside";
import { editorialCategories, getImplementedTypes } from "../../config/editorialRegistry";
import { getTypeIcon } from "../editorial/EditorialTypeSelector";

export default function FeedsMenu() {
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const menuRef = useRef(null);

  useClickOutside(menuRef, () => {
    setVisible(false);
    setActiveCategory(null);
    setSearchTerm("");
  });

  // Get all implemented types
  const allTypes = getImplementedTypes().filter((type) => type.id !== "facebook");

  // Filter types based on search or category
  const filteredTypes = searchTerm
    ? allTypes.filter(
        (type) =>
          type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          type.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : activeCategory
    ? editorialCategories
        .find((cat) => cat.id === activeCategory)
        ?.types.filter((type) => type.implemented && type.id !== "facebook") || []
    : allTypes;

  return (
    <div className="feeds_menu_container" ref={menuRef}>
      <div
        className={`circle_icon hover1 ${visible ? "active_header" : ""}`}
        onClick={() => setVisible(!visible)}
        title="Explorar Feeds"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
        </svg>
      </div>

      {visible && (
        <div className="feeds_menu">
          <div className="feeds_menu_header">
            <h3>Explorar Feeds</h3>
            <div className="feeds_search">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar feeds..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (e.target.value) setActiveCategory(null);
                }}
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="feeds_category_tabs">
            <button
              className={`feeds_category_tab ${!activeCategory && !searchTerm ? "active" : ""}`}
              onClick={() => {
                setActiveCategory(null);
                setSearchTerm("");
              }}
            >
              <span className="tab_emoji">🌐</span>
              <span>Todos</span>
            </button>
            {editorialCategories.map((category) => (
              <button
                key={category.id}
                className={`feeds_category_tab ${activeCategory === category.id ? "active" : ""}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSearchTerm("");
                }}
                style={{ "--cat-color": category.color }}
              >
                <span className="tab_emoji">{category.emoji}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {/* Category Header when selected */}
          {activeCategory && !searchTerm && (
            <div className="feeds_active_category">
              {(() => {
                const cat = editorialCategories.find((c) => c.id === activeCategory);
                return (
                  <>
                    <span className="category_emoji">{cat?.emoji}</span>
                    <span className="category_name">{cat?.name}</span>
                    <span className="category_count">{filteredTypes.length} feeds</span>
                  </>
                );
              })()}
            </div>
          )}

          {/* Search Results Header */}
          {searchTerm && (
            <div className="feeds_search_results">
              <span>Resultados para "{searchTerm}"</span>
              <span className="results_count">{filteredTypes.length} encontrados</span>
            </div>
          )}

          {/* Feeds Grid */}
          <div className="feeds_menu_grid">
            {filteredTypes.length > 0 ? (
              filteredTypes.map((feed) => (
                <Link
                  key={feed.id}
                  to={`/${feed.id.replace(/_/g, "-")}`}
                  className="feeds_menu_item"
                  onClick={() => {
                    setVisible(false);
                    setActiveCategory(null);
                    setSearchTerm("");
                  }}
                  style={{ "--feed-color": feed.color }}
                  title={feed.description}
                >
                  <div className="feeds_menu_icon">{getTypeIcon(feed.id)}</div>
                  <div className="feeds_menu_info">
                    <span className="feeds_menu_name">{feed.name}</span>
                    <span className="feeds_menu_desc">{feed.description}</span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="feeds_no_results">
                <span>Nenhum feed encontrado</span>
              </div>
            )}
          </div>

          {/* Quick Access Footer */}
          <div className="feeds_menu_footer">
            <Link to="/playlists" onClick={() => setVisible(false)}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
              </svg>
              Minhas Playlists
            </Link>
            <Link to="/ai" onClick={() => setVisible(false)}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
              Assistentes IA
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
